import { autoinject } from 'aurelia-dependency-injection';
import { bindable } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { CustomerInfo, ContractLine, StatusEnum } from '../../services/customer-info-store';
import { OrganizationInfo } from '../../services/organization-info';
import { formatLog } from '../../utils/formatter';

@autoinject
export class LicenseStatus {

    constructor(private router: Router, private orgInfo: OrganizationInfo) {
    }

    @bindable customer: CustomerInfo | null = null;
    @bindable organizationid: string = "";
    @bindable satellitemode : boolean = false;
    @bindable hidechartbtn: boolean = false;
    @bindable chartonly: boolean = false;
    contractLines: ContractLine[] = [];
    isLoaded: Boolean = false;


    bind() {
        console.log(formatLog(`Getting customerinfo from store (id=${this.organizationid})`));

        this.contractLines = this.customer!.contractLines || [];

        this.init();
    }

    private async init() {
        console.log(formatLog(`Number of contract-lines: ${this.contractLines.length}`));

        try {

            for (let contractLine of this.contractLines) {
                if (contractLine.usageBundle && contractLine.usageBundle.startDate) {
                    console.log(formatLog("Initializing consumption based tile"));
                    this.setContractLineStatusConsumptionBased(contractLine);

                    this.setPeriodInfo(contractLine);
                    
                    this.setPercentageUsedConsumptionBased(contractLine);

                    this.setPlannedUsedToday(contractLine);
                }
                else {
                    console.log(formatLog("Initializing license based tile"));
                    if (this.satellitemode === false && contractLine.usageQuery) {
                        console.log(formatLog("Retrieving usage from CRM"));
                        contractLine.usage = await this.orgInfo.retrieveUsage(contractLine.usageQuery);
                    }
                    else {
                        console.log(formatLog("Show environment usage from backend"));
                        contractLine.usage = contractLine.usageByEnvironment; // Usage for this specific environment, save into 'usage' as this will be used later on
                    }
                    
                    this.setContractLineStatusLicenseBased(contractLine);

                    this.setPercentageUsedLicenseBased(contractLine);
                }
            }

            this.isLoaded = true;
        }
        catch (e) {
            console.log(formatLog("Error while initializing license-status component"), e);
            this.router
                .navigateToRoute('error', { errorMessage: `Error retrieving license-status: ${e}` });
        }
    }

    setPlannedUsedToday(contractLine: ContractLine) {
        if (contractLine.usageBundle.periodPercentage) {
            const transferred = contractLine.usageBundle.transferred || 0;
            const total = contractLine.quantity - transferred;
            const plannedToday = (contractLine.usageBundle.periodPercentage / 100) * total;

            contractLine.usageBundle.plannedUsedToday = Math.round(plannedToday || 0);
        }
    }

    setContractLineStatusLicenseBased(contractLine: ContractLine) {
        if (contractLine.usage > contractLine.quantity) {
            contractLine.status = StatusEnum.Error;
        }
        else if (contractLine.usage == contractLine.quantity) {
            contractLine.status = StatusEnum.Warn;
        }
        else {
            contractLine.status = StatusEnum.Ok;
        }
    }

    setPercentageUsedLicenseBased(contractLine: ContractLine) {
        const usage = contractLine.usage || 0;
        if (contractLine.quantity > 0 && usage >= contractLine.quantity) {
            contractLine.licensesUsedPercentage = 100;
        } else if (usage > 0) {
            contractLine.licensesUsedPercentage = Math.round((usage / contractLine.quantity) * 100);
        } else {
            contractLine.licensesUsedPercentage = 0;
        }

        if (usage > contractLine.quantity) {
            contractLine.isExceeded = true;
        }
    }

    setPeriodInfo(contractLine: ContractLine) {
        if (contractLine.usageBundle.startDate && contractLine.usageBundle.endDate) {
            const startDate = new Date(contractLine.usageBundle.startDate);
            const endDate = new Date(contractLine.usageBundle.endDate);
            contractLine.usageBundle.startDateFormatted = `${('0' + startDate.getDate()).slice(-2)}-${('0' + (startDate.getMonth() + 1)).slice(-2)}-${startDate.getFullYear()}`;
            contractLine.usageBundle.endDateFormatted = `${('0' + endDate.getDate()).slice(-2)}-${('0' + (endDate.getMonth() + 1)).slice(-2)}-${endDate.getFullYear()}`;;

            const period = (endDate.getTime() - startDate.getTime()) + 1;
            const elapsed = new Date().getTime() - startDate.getTime();
            contractLine.usageBundle.periodPercentage = Math.round((elapsed / period) * 100);
        }
    }

    setPercentageUsedConsumptionBased(contractLine: ContractLine) {
        if (contractLine.quantity > 0 && contractLine.usageBundle.total >= contractLine.quantity) {
            contractLine.usageBundle.usedPercentage = 100;
        } else if (contractLine.usageBundle.total > 0) {
            contractLine.usageBundle.usedPercentage = Math.round((contractLine.usageBundle.total / contractLine.quantity) * 100);
        } else {
            contractLine.usageBundle.usedPercentage = 0;
        }

        if (contractLine.usageBundle.usedPercentage === 0 && contractLine.usageBundle.current > 0) {
            contractLine.usageBundle.usedPercentage = 1; // To visualize that there is at least some usage (this scenario occurs when there is a negative usage transferred)
        }

        if (contractLine.usageBundle.total > contractLine.quantity) {
            contractLine.isExceeded = true;
        }
    }

    setContractLineStatusConsumptionBased(contractLine: ContractLine) {
        if (contractLine.usageBundle.total > contractLine.quantity) {
            contractLine.status = StatusEnum.Error;
        }
        else if (contractLine.usageBundle.forecast >= contractLine.quantity) {
            contractLine.status = StatusEnum.Warn;
        }
        else {
            contractLine.status = StatusEnum.Ok;
        }
    }
}



// WEBPACK FOOTER //
// ./ClientApp/app/components/licensing-overview/license-status.ts